import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HistoryIcon, XIcon, RotateCcwIcon, Trash2Icon, ChevronRightIcon, ClockIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { VersionSnapshot, getSnapshots, deleteSnapshot, formatTimestamp } from '../../../lib/versionHistory';
interface VersionHistoryPanelProps {
  contentKey: string;
  onRestore: (data: any) => void;
  isOpen: boolean;
  onClose: () => void;
}
export function VersionHistoryPanel({
  contentKey,
  onRestore,
  isOpen,
  onClose
}: VersionHistoryPanelProps) {
  const [snapshots, setSnapshots] = useState<VersionSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getSnapshots(contentKey).then(s => {
        setSnapshots(s);
        setLoading(false);
      });
    }
  }, [isOpen, contentKey]);
  const handleRestore = (snapshot: VersionSnapshot) => {
    setRestoring(snapshot.id);
    setTimeout(() => {
      onRestore(snapshot.data);
      setRestoring(null);
      onClose();
    }, 300);
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this version?')) return;
    await deleteSnapshot(contentKey, id);
    setSnapshots(prev => prev.filter(s => s.id !== id));
  };
  return <AnimatePresence data-id="element-1112">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} data-id="element-1113" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-background border-l border-border z-[81] flex flex-col" data-id="element-1114">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border" data-id="element-1115">
              <div className="flex items-center gap-3" data-id="element-1116">
                <HistoryIcon className="w-5 h-5 text-primary" data-id="element-1117" />
                <div data-id="element-1118">
                  <h2 className="font-display text-lg font-bold" data-id="element-1119">
                    Version History
                  </h2>
                  <p className="font-mono text-xs text-muted-foreground" data-id="element-1120">
                    {snapshots.length} version
                    {snapshots.length !== 1 ? 's' : ''} saved
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Close" data-id="element-1121">
                <XIcon className="w-5 h-5" data-id="element-1122" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-1123">
              {loading ? <div className="p-8 text-center font-mono text-sm text-muted-foreground" data-id="element-1124">
                  Loading history...
                </div> : snapshots.length === 0 ? <div className="p-8 text-center" data-id="element-1125">
                  <ClockIcon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-4" data-id="element-1126" />
                  <p className="font-mono text-sm text-muted-foreground" data-id="element-1127">
                    No versions saved yet.
                  </p>
                  <p className="font-mono text-xs text-muted-foreground/60 mt-2" data-id="element-1128">
                    Versions are created automatically when you save changes.
                  </p>
                </div> : <div className="divide-y divide-border" data-id="element-1129">
                  {snapshots.map((snapshot, index) => <div key={snapshot.id} className="group" data-id="element-1130">
                      <button onClick={() => setExpandedId(expandedId === snapshot.id ? null : snapshot.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/50 transition-colors" data-id="element-1131">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${index === 0 ? 'bg-primary' : 'bg-muted-foreground/30'}`} data-id="element-1132" />
                        <div className="flex-1 min-w-0" data-id="element-1133">
                          <div className="flex items-center gap-2" data-id="element-1134">
                            <span className="font-mono text-sm font-medium truncate" data-id="element-1135">
                              {snapshot.label}
                            </span>
                            {index === 0 && <span className="font-mono text-[10px] uppercase tracking-widest text-primary bg-primary/10 px-1.5 py-0.5" data-id="element-1136">
                                Latest
                              </span>}
                          </div>
                          <span className="font-mono text-xs text-muted-foreground" data-id="element-1137">
                            {formatTimestamp(snapshot.timestamp)}
                          </span>
                        </div>
                        <ChevronRightIcon className={`w-4 h-4 text-muted-foreground transition-transform ${expandedId === snapshot.id ? 'rotate-90' : ''}`} data-id="element-1138" />
                      </button>

                      <AnimatePresence data-id="element-1139">
                        {expandedId === snapshot.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-1140">
                            <div className="px-4 pb-4 flex items-center gap-2" data-id="element-1141">
                              <Button size="sm" onClick={() => handleRestore(snapshot)} className="gap-1.5 flex-1" disabled={restoring === snapshot.id} data-id="element-1142">
                                <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-1143" />
                                {restoring === snapshot.id ? 'Restoring...' : 'Restore this version'}
                              </Button>
                              <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => handleDelete(snapshot.id)} data-id="element-1144">
                                <Trash2Icon className="w-3.5 h-3.5" data-id="element-1145" />
                              </Button>
                            </div>

                            {/* Data preview */}
                            <div className="px-4 pb-4" data-id="element-1146">
                              <div className="bg-secondary/50 border border-border p-3 max-h-40 overflow-y-auto" data-id="element-1147">
                                <pre className="font-mono text-[10px] text-muted-foreground whitespace-pre-wrap break-all" data-id="element-1148">
                                  {JSON.stringify(snapshot.data, null, 2).slice(0, 500)}
                                  {JSON.stringify(snapshot.data).length > 500 ? '...' : ''}
                                </pre>
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </div>}
            </div>

            {/* Footer */}
            {snapshots.length > 0 && <div className="p-4 border-t border-border" data-id="element-1149">
                <p className="font-mono text-[10px] text-muted-foreground/60 text-center" data-id="element-1150">
                  Up to 25 versions are kept. Oldest versions are automatically
                  removed.
                </p>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}
// Reusable button to open history panel
export function VersionHistoryButton({
  onClick
}: {
  onClick: () => void;
}) {
  return <Button variant="ghost" onClick={onClick} className="gap-2" data-id="element-1151">
      <HistoryIcon className="w-4 h-4" data-id="element-1152" />
      History
    </Button>;
}